import React, { createContext, useContext, useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { setAuthToken, setTokenRefresher } from "../api";
import { setAdminAuthHeaders } from "../adminApi";

export interface UserInfo {
  email: string;
  name: string;
  token: string;
}

interface AuthContextType {
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (isAuthenticated && accounts.length > 0) {
      initSession();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress, accounts]);

  const initSession = async () => {
    try {
      setLoading(true);
      const account = instance.getActiveAccount() || accounts[0];
      if (!account) {
        setLoading(false);
        return;
      }
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const email = account.username || "";
      const name = account.name || email;

      setAuthToken(tokenResponse.idToken);
      const refreshToken = async () => {
        const resp = await instance.acquireTokenSilent({ ...loginRequest, account });
        return resp.idToken;
      };
      setTokenRefresher(refreshToken);
      setAdminAuthHeaders(async () => {
        const token = await refreshToken();
        return { Authorization: `Bearer ${token}` };
      });

      setUser({ email, name, token: tokenResponse.idToken });
      setError(null);
    } catch (err: any) {
      setError("Failed to authenticate");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Clears any leftover `*.interaction.status` key MSAL stores in sessionStorage.
  // An interrupted prior redirect (or the wrong app previously on this domain) can
  // leave it set even though React's `inProgress` reads None — which makes the next
  // loginRedirect throw BrowserAuthError: interaction_in_progress.
  const clearStaleInteraction = () => {
    try {
      Object.keys(sessionStorage)
        .filter((k) => k.toLowerCase().includes("interaction.status"))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch {
      /* sessionStorage may be unavailable; ignore */
    }
  };

  const login = () => {
    if (inProgress !== InteractionStatus.None) return; // already mid-interaction
    instance.loginRedirect(loginRequest).catch((e: any) => {
      if (e?.errorCode === "interaction_in_progress") {
        // Stale status from an interrupted attempt — clear it and retry once.
        clearStaleInteraction();
        instance.loginRedirect(loginRequest).catch((e2: any) => {
          console.error("loginRedirect retry failed:", e2?.errorCode || e2);
          setError("Sign-in could not start. Please clear site data and try again.");
        });
        return;
      }
      console.error("loginRedirect failed:", e?.errorCode || e);
      setError("Sign-in could not start. Please refresh the page and try again.");
    });
  };

  const logout = () => {
    instance.logoutRedirect().catch((e: any) => {
      console.error("logoutRedirect failed:", e?.errorCode || e);
    });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
